import React from 'react';
import {
  Box,
  Typography,
  Paper,
  Chip,
  IconButton,
  Tooltip,
} from '@mui/material';
import {
  SmartToy as BotIcon,
  Person as PersonIcon,
  Error as ErrorIcon,
  ContentCopy as CopyIcon,
} from '@mui/icons-material';
import { Prism as SyntaxHighlighter } from 'react-syntax-highlighter';
import { materialLight } from 'react-syntax-highlighter/dist/esm/styles/prism';
import { toast } from 'react-toastify';
import { Message } from '../types/chat';

interface ChatMessageProps {
  message: Message;
}

const ChatMessage: React.FC<ChatMessageProps> = ({ message }) => {
  const isUser = message.role === 'user';
  const isError = message.isError;

  const formatTimestamp = (timestamp: Date) => {
    return timestamp.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  };

  const renderCodeSnippet = (snippet: any, index: number) => {
    const handleCopyCode = async () => {
      try {
        await navigator.clipboard.writeText(snippet.code);
        toast.success('Code copied to clipboard!', {
          position: 'top-right',
          autoClose: 2000,
          hideProgressBar: false,
          closeOnClick: true,
          pauseOnHover: true,
          draggable: true,
        });
      } catch (err) {
        console.error('Failed to copy code:', err);
        toast.error('Failed to copy code to clipboard', {
          position: 'top-right',
          autoClose: 3000,
        });
      }
    };

    // Map language names to syntax highlighter languages
    const getLanguage = (lang: string) => {
      const languageMap: { [key: string]: string } = {
        'python': 'python',
        'javascript': 'javascript',
        'typescript': 'typescript',
        'java': 'java',
        'csharp': 'csharp',
        'php': 'php',
        'ruby': 'ruby',
        'go': 'go',
        'json': 'json',
        'xml': 'xml',
        'html': 'html',
        'css': 'css',
        'bash': 'bash',
        'shell': 'bash',
        'sql': 'sql',
        'yaml': 'yaml',
        'yml': 'yaml',
        'markdown': 'markdown',
        'md': 'markdown',
      };
      return languageMap[lang.toLowerCase()] || 'text';
    };

    return (
      <Box
        key={index}
        sx={{
          mt: 2,
          bgcolor: 'grey.50',
          borderRadius: 2,
          overflow: 'hidden',
          border: 1,
          borderColor: 'grey.200',
        }}
      >
        <Box
          sx={{
            bgcolor: 'grey.100',
            px: 2,
            py: 1.5,
            borderBottom: 1,
            borderColor: 'grey.200',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
          }}
        >
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flex: 1 }}>
            <Typography variant="body2" fontWeight="medium" color="text.primary">
              {snippet.filename || `${snippet.language} Code`}
            </Typography>
            {snippet.language && (
              <Chip
                label={snippet.language}
                size="small"
                variant="outlined"
                sx={{ fontSize: '0.75rem', ml: 1 }}
              />
            )}
          </Box>
          <Tooltip title="Copy code">
            <IconButton
              size="small"
              onClick={handleCopyCode}
              sx={{
                ml: 1,
                '&:hover': {
                  bgcolor: 'grey.200',
                },
              }}
            >
              <CopyIcon sx={{ fontSize: 16 }} />
            </IconButton>
          </Tooltip>
        </Box>
        {snippet.description && (
          <Box sx={{ px: 2, py: 1, bgcolor: 'grey.50', borderBottom: 1, borderColor: 'grey.200' }}>
            <Typography variant="caption" color="text.secondary">
              {snippet.description}
            </Typography>
          </Box>
        )}
        <Box sx={{ position: 'relative' }}>
          <SyntaxHighlighter
            language={getLanguage(snippet.language)}
            style={materialLight}
            customStyle={{
              margin: 0,
              borderRadius: 0,
              fontSize: '0.875rem',
              lineHeight: 1.5,
            }}
            showLineNumbers={true}
            wrapLines={true}
            lineNumberStyle={{
              color: '#999',
              fontSize: '0.75rem',
              minWidth: '2.5em',
            }}
          >
            {snippet.code}
          </SyntaxHighlighter>
        </Box>
      </Box>
    );
  };

  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'flex-start',
        gap: 2,
        justifyContent: isUser ? 'flex-end' : 'flex-start',
      }}
    >
      {!isUser && (
        <Box
          sx={{
            width: 40,
            height: 40,
            borderRadius: '50%',
            bgcolor: 'primary.50',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            flexShrink: 0,
          }}
        >
          {isError ? (
            <ErrorIcon sx={{ fontSize: 20, color: 'error.main' }} />
          ) : (
            <BotIcon sx={{ fontSize: 20, color: 'primary.main' }} />
          )}
        </Box>
      )}
      
      <Box
        sx={{
          flex: 1,
          maxWidth: '80%',
          order: isUser ? -1 : 0,
        }}
      >
        <Paper
          elevation={1}
          sx={{
            p: 3,
            borderRadius: 2,
            bgcolor: isUser 
              ? 'primary.main' 
              : isError 
                ? 'error.50' 
                : 'background.paper',
            border: isError ? 1 : 0,
            borderColor: isError ? 'error.200' : 'transparent',
          }}
        >
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1.5 }}>
            <Typography
              variant="body2"
              fontWeight="medium"
              color={isUser ? 'primary.contrastText' : 'text.primary'}
            >
              {isUser ? 'You' : isError ? 'Error' : 'AI Assistant'}
            </Typography>
            <Typography
              variant="caption"
              color={isUser ? 'primary.100' : 'text.secondary'}
            >
              {formatTimestamp(message.timestamp)}
            </Typography>
          </Box>
          
          <Typography
            variant="body1"
            color={isUser ? 'primary.contrastText' : 'text.primary'}
            sx={{
              whiteSpace: 'pre-wrap',
              mb: message.codeSnippets && message.codeSnippets.length > 0 ? 2 : 0,
              minHeight: '1.5em', // Ensure space even when empty
            }}
            key={message.content.length} // Force re-render when content changes
          >
            {message.content || (message.role === 'assistant' && !message.content ? '...' : '')}
          </Typography>
          
          {/* Show streaming status with token count */}
          {message.role === 'assistant' && message.status === 'streaming' && message.timingInfo?.tokenCount && (
            <Box sx={{ mt: 1.5 }}>
              <Typography variant="caption" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                ✨ Streaming... {message.timingInfo.tokenCount} token{message.timingInfo.tokenCount !== 1 ? 's' : ''} received
                {message.timingInfo.timeToFirstToken && (
                  <> • First token: {message.timingInfo.timeToFirstToken < 1 
                    ? `${(message.timingInfo.timeToFirstToken * 1000).toFixed(0)}ms` 
                    : `${message.timingInfo.timeToFirstToken.toFixed(2)}s`}</>
                )}
              </Typography>
            </Box>
          )}
          
          {/* Show timing info for completed messages */}
          {message.role === 'assistant' && message.status === 'complete' && message.timingInfo && !message.isError && (
            <Box sx={{ mt: 2, pt: 2, borderTop: 1, borderColor: 'divider' }}>
              <Typography variant="caption" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                ✅ Response generated in {message.timingInfo.totalTime && (
                  message.timingInfo.totalTime < 1 
                    ? `${(message.timingInfo.totalTime * 1000).toFixed(0)}ms` 
                    : `${message.timingInfo.totalTime.toFixed(2)}s`
                )}
                {message.timingInfo.timeToFirstToken && message.timingInfo.timeToFirstToken !== message.timingInfo.totalTime && (
                  <> • First token: {message.timingInfo.timeToFirstToken < 1 
                    ? `${(message.timingInfo.timeToFirstToken * 1000).toFixed(0)}ms` 
                    : `${message.timingInfo.timeToFirstToken.toFixed(2)}s`}</>
                )}
                {message.timingInfo.tokenCount && (
                  <> • {message.timingInfo.tokenCount} token{message.timingInfo.tokenCount !== 1 ? 's' : ''}</>
                )}
              </Typography>
            </Box>
          )}
          
          {message.codeSnippets && message.codeSnippets.length > 0 && (
            <Box sx={{ mt: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
              {message.codeSnippets.map((snippet, index) => 
                renderCodeSnippet(snippet, index)
              )}
            </Box>
          )}
        </Paper>
      </Box>
      
      {isUser && (
        <Box
          sx={{
            width: 40,
            height: 40,
            borderRadius: '50%',
            bgcolor: 'grey.200',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            flexShrink: 0,
          }}
        >
          <PersonIcon sx={{ fontSize: 20, color: 'grey.600' }} />
        </Box>
      )}
    </Box>
  );
};

export default ChatMessage;
